import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AiService } from 'src/app/models/api/agentStep.model';
import { IntegrationModel } from 'src/app/models/api';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';

export interface AgentNavigationContext {
  inventoryId?: string | null;
  resourceId?: string | null;
  service: AiService;
  integration?: string | IntegrationModel;
}

export interface ActorNavigationContext {
  actorInventoryId?: string | null;
  integration?: string | IntegrationModel;
}

export const DEFAULT_GPT_ID = 'Chat GPT';

/**
 * Service to handle agent inventory navigation logic across different components.
 * Centralizes the logic for determining if an agent link should be shown and how to navigate.
 */
@Injectable({
  providedIn: 'root',
})
export class AgentInventoryNavigationService {
  constructor(
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _sidePanel: SidePanelService,
    private readonly _router: Router
  ) {}

  /**
   * Checks if the agent link should be shown based on feature flags and available data
   */
  public shouldShowAgentLink(context: AgentNavigationContext): boolean {
    const { inventoryId, resourceId, service } = context;
    const isEnrichmentEnabled = this._isInventoryEnrichmentEnabled();

    if (isEnrichmentEnabled && inventoryId != null && inventoryId.trim() !== '') {
      return true;
    }

    if (
      service === AiService.copilotStudio ||
      service === AiService.chatGptEnterprise ||
      service === AiService.vertexAi ||
      service === AiService.serviceNow
    ) {
      return resourceId != null && resourceId.trim() !== '' && resourceId !== DEFAULT_GPT_ID;
    }

    return false;
  }

  /**
   * Navigates to the agent in inventory or opens inventory drawer if inventoryId exists
   */
  public navigateToAgent(context: AgentNavigationContext): void {
    const { inventoryId, resourceId, integration } = context;
    const isEnrichmentEnabled = this._isInventoryEnrichmentEnabled();

    if (isEnrichmentEnabled && inventoryId != null && inventoryId.trim() !== '') {
      const integrationId = typeof integration === 'string' ? integration : integration?.id;
      if (integrationId) {
        this._sidePanel.openResourceDetailsFromIssue(inventoryId, integrationId);
      }
      return;
    }

    if (!resourceId) return;

    const queryParam = `search:"${resourceId}" category:bot`;
    this._router.navigate(['app/inventory'], {
      queryParams: { q: queryParam },
    });
  }

  /**
   * Checks if the actor link should be shown based on feature flags and inventoryId
   */
  public shouldShowActorLink(context: ActorNavigationContext): boolean {
    const { actorInventoryId } = context;
    const isEnrichmentEnabled = this._isInventoryEnrichmentEnabled();

    return isEnrichmentEnabled && actorInventoryId != null && actorInventoryId.trim() !== '';
  }

  /**
   * Navigates to the actor in inventory drawer
   */
  public navigateToActor(context: ActorNavigationContext): void {
    const { actorInventoryId, integration } = context;

    if (!actorInventoryId || actorInventoryId.trim() === '') return;

    const integrationId = typeof integration === 'string' ? integration : integration?.id;
    if (integrationId) {
      this._sidePanel.openResourceDetailsFromIssue(actorInventoryId, integrationId);
    }
  }

  private _isInventoryEnrichmentEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.DrInventoryEnrichment);
  }
}
